// Aviso de las copias al caso desde mi buzon personal (capa 3, decision de Johan: automatico con aviso). Cuando la app
// copia al caso lo que escribi con el cliente desde mi buzon, veo «Tu respuesta a Bestsol se agregó al caso #N ·
// Deshacer» en la campana y en el propio caso, durante 24 h. Deshacer borra las copias, separa el hilo y no se vuelve a
// unir solo.
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { type CopiaReciente, deshacerCopia, misCopiasRecientes } from '../api/casos';
import { useSession } from '../auth/session';
import { Icon } from '../lib/icons';
import { refrescarCasos } from '../screens/bandeja/useVistas';
import { toast } from './toasts';

// Una sola consulta compartida (campana y caso). Sin los campos nuevos (app sin publicar) la consulta falla: no se
// reintenta y simplemente no hay avisos.
export function useCopiasRecientes() {
  const { yo } = useSession();
  return useQuery({
    queryKey: ['copias-recientes', yo.miembroId],
    queryFn: () => misCopiasRecientes(yo.miembroId!),
    enabled: !!yo.miembroId,
    refetchInterval: 2 * 60_000,
    staleTime: 30_000,
    retry: false,
  });
}

export const textoCopia = (c: Pick<CopiaReciente, 'cliente' | 'numero' | 'cuantas'>) => `${c.cuantas === 1 ? 'Tu respuesta' : `Tus ${c.cuantas} respuestas`} a ${c.cliente} se ${c.cuantas === 1 ? 'agregó' : 'agregaron'} al caso #${c.numero ?? '—'}`;

export function useDeshacerCopia() {
  const qc = useQueryClient();
  const { yo } = useSession();
  const [deshaciendo, setDeshaciendo] = useState<string | null>(null);
  const deshacer = async (c: CopiaReciente) => {
    setDeshaciendo(`${c.casoId}:${c.hiloId}`);
    try {
      const n = await deshacerCopia(c.casoId, c.hiloId, yo.nombre);
      toast(n ? `Listo: se quitó del caso #${c.numero ?? '—'} y tu correo quedó separado.` : 'Tu correo quedó separado del caso.');
      void qc.invalidateQueries({ queryKey: ['copias-recientes'] });
      void qc.invalidateQueries({ queryKey: ['mensajes-caso', c.casoId] });
      void qc.invalidateQueries({ queryKey: ['ocultos-caso', c.casoId] });
      refrescarCasos(qc);
    } catch (e) {
      toast(`No se pudo deshacer: ${(e as Error).message}`, 'error');
    } finally {
      setDeshaciendo(null);
    }
  };
  return { deshacer, deshaciendo };
}

// En el caso: el aviso para quien es el buzon, mientras puede deshacerlo.
export function AvisoCopia({ casoId }: { casoId: string }) {
  const r = useCopiasRecientes();
  const { deshacer, deshaciendo } = useDeshacerCopia();
  const mias = (r.data ?? []).filter((c) => c.casoId === casoId);
  if (!mias.length) return null;
  return (
    <>
      {mias.map((c) => (
        <section key={c.hiloId} className="aviso-copia" role="status" aria-label="Respuesta agregada al caso">
          <Icon name="mail" size={15} />
          <span>
            <b>{textoCopia(c)}</b>
            <span className="sb"> · Lo ve todo el grupo. Solo se copió lo que escribiste con el cliente; puedes deshacerlo durante 24 h.</span>
          </span>
          <button className="btn sm" disabled={deshaciendo === `${c.casoId}:${c.hiloId}`} onClick={() => void deshacer(c)}>
            {deshaciendo === `${c.casoId}:${c.hiloId}` ? 'Deshaciendo…' : 'Deshacer'}
          </button>
        </section>
      ))}
    </>
  );
}
